'use client'
import { useMemo, useRef, useEffect } from 'react'
import { sendGTMEvent } from '@next/third-parties/google'
import { Order, Product } from '@/types/commerce'
import type { IProfile } from '@/types/profile'
import type { IUser } from '@/types/auth'
import { getLocalStorageData } from '@/utils/handleLocalStorage'
import { buildGtmIdentityPush } from '@/utils/analytics/gtmIdentity'
import { useUser } from '@/hooks/users/useUser'
import { useGeolocation } from '@/hooks/geolocation/useGeolocation'
import { isIdentifiedUser, type UserRole } from '@/constants/roles'

import { debug } from '@/utils/debug'

import settings from '@/settings/public'

/**
 * GTM Event Types
 *
 * Naming Convention: {CATEGORY}_{SUBJECT}_{ACTION}
 * - CATEGORY: Domain area (USER, PLAYER, CHECKOUT, SUBSCRIPTION, UI, PAGE)
 * - SUBJECT: Specific feature (SSO, PLAYBACK, PLAN, BANNER)
 * - ACTION: Past tense verb (STARTED, COMPLETED, FAILED, CLICKED)
 *
 * Payload convention: reuse the generic `GtmDataVariables` keys (`action`,
 * `name`, `type`, `id`, `provider`, `path`, `error`) instead of adding
 * event-specific keys — the GTM container maps dataLayer variables once, so
 * new events must not require new variables there. Per-event meanings:
 * - RETENTION_OFFER_*: `name` = couponCode (identifies the A/B arm — each arm
 *   ships a distinct coupon, PRO-213), `type` = applied AB cinematic tag
 *   (undefined on the base cinematic).
 * - USER_PHONE_*: `type` = the surface that asked ('spotlight' or 'profile'),
 *   so one pair of triggers covers every place the number can be captured.
 *
 * `EVENT_GATES.resolved` asks only that a backend profile came back — an anonymous
 * `Guest` session satisfies it and still fires. Deliberately NOT "identified",
 * which elsewhere in this file means "role is User": gating these 20 events on the
 * role would stop them for anonymous sessions, under 13 Google Ads conversion tags.
 * Whether the gate should move to the role is its own question, for its own ticket.
 */
const EVENT_GATES = { resolved: 'resolved', any: 'any' } as const
type EventGate = (typeof EVENT_GATES)[keyof typeof EVENT_GATES]

const GTM_EVENTS = {
  USER_SESSION_IDENTIFIED: { requires: EVENT_GATES.resolved },
  USER_AUTH_SUCCEEDED: { requires: EVENT_GATES.resolved },
  USER_REGISTER_SUCCEEDED: { requires: EVENT_GATES.resolved },
  USER_LOGOUT_COMPLETED: { requires: EVENT_GATES.resolved },
  CHECKOUT_FLOW_STARTED: { requires: EVENT_GATES.resolved },
  CHECKOUT_ITEM_VIEWED: { requires: EVENT_GATES.resolved },
  CHECKOUT_PLAN_SELECTED: { requires: EVENT_GATES.resolved },
  CHECKOUT_PAYMENT_FORM_INITIALIZED: { requires: EVENT_GATES.resolved },
  CHECKOUT_PURCHASE_COMPLETED: { requires: EVENT_GATES.resolved },
  CHECKOUT_PURCHASE_FAILED: { requires: EVENT_GATES.resolved },
  SUBSCRIPTION_CANCELLATION_FORM_VIEW: { requires: EVENT_GATES.resolved },
  SUBSCRIPTION_CANCELED: { requires: EVENT_GATES.resolved },
  SUBSCRIPTION_PAUSED: { requires: EVENT_GATES.resolved },
  SUBSCRIPTION_RESUBSCRIBED: { requires: EVENT_GATES.resolved },
  SUBSCRIPTION_RESUMED: { requires: EVENT_GATES.resolved },
  RETENTION_OFFER_VIEW: { requires: EVENT_GATES.resolved },
  RETENTION_OFFER_ACCEPTED: { requires: EVENT_GATES.resolved },
  RETENTION_OFFER_DECLINED: { requires: EVENT_GATES.resolved },
  PROMO_COUPON_REDEEMED: { requires: EVENT_GATES.resolved },
  FEEDBACK_RATING: { requires: EVENT_GATES.resolved },
  USER_PHONE_PROMPT_VIEWED: { requires: EVENT_GATES.resolved },
  USER_PHONE_PROMPT_DISMISSED: { requires: EVENT_GATES.resolved },
  USER_PHONE_SAVED: { requires: EVENT_GATES.resolved },
  PAGE_VIEW: { requires: EVENT_GATES.any },
  UI_BANNER_CLICKED: { requires: EVENT_GATES.any },
  UI_BANNER_VIEWED: { requires: EVENT_GATES.any },
  UI_BANNER_DISMISSED: { requires: EVENT_GATES.any },
  USER_AUTH_FAILED: { requires: EVENT_GATES.any },
  USER_REGISTER_FAILED: { requires: EVENT_GATES.any },
  USER_SSO_FLOW_STARTED: { requires: EVENT_GATES.any },
  USER_SSO_CALLBACK_RECEIVED: { requires: EVENT_GATES.any },
  USER_SSO_AUTH_COMPLETED: { requires: EVENT_GATES.any },
  USER_SSO_AUTH_FAILED: { requires: EVENT_GATES.any },
  PLAYER_PLAYBACK_STARTED: { requires: EVENT_GATES.any },
  PLAYER_PLAYBACK_FAILED: { requires: EVENT_GATES.any },
  PLAYER_CAST_STARTED: { requires: EVENT_GATES.any },
  UI_ERROR_CAPTURED: { requires: EVENT_GATES.any },
} as const satisfies Record<string, { requires: EventGate }>

export type GtmEventType = keyof typeof GTM_EVENTS

export type GtmDataVariables = {
  action: string
  error: string
  id: string
  name: string
  provider: string
  path?: string
  type: string | string[]
  user?: GtmIdentityInput
  product?: Product
  order?: Order
}

export type SendGtm = Record<GtmEventType, (data: Partial<GtmDataVariables>) => void>

/** The emitted `user` object: a published contract with the GTM containers. */
type GtmUserPayload = {
  id: string | null
  email: string | null
  country: string | null
  language: string | null
  role: UserRole | null
  gaId: string | null
}

/** An already-flattened identity, as a caller may hand it to `sendGtm.*`. */
type FlatGtmUser = Partial<GtmUserPayload>

type GtmIdentityInput = IProfile | IUser | FlatGtmUser | null | undefined

type GtmIdentityFields = Omit<GtmUserPayload, 'id' | 'role'>

/**
 * `anonymous` carries no `id` key at all, which is what makes an emittable
 * anonymous id unrepresentable rather than something to strip before emission.
 * An anonymous session does have a real profile — the backend answers with a
 * `Guest` role and its own ObjectId, stable for the session — and emitting that
 * id feeds GA4's `user_id` (`{{[NW] User ID}}` on Fanatiz prod) a visitor who
 * never logged in. It also survives logout: measured on dev 2026-09-24, the
 * `PAGE_VIEW` after a logout overwrote the `user: null` that `clearGtmUserData`
 * had written eleven messages earlier.
 *
 * `role` is non-nullable only on `identified`: a profile carrying an `_id` with
 * no `rbac.role` lands in `anonymous` and emits `role: null`.
 *
 * All three variants carry the remaining fields, including `unresolved`: the status
 * decides only whether a user-required event fires, never what an event that does
 * fire carries. A caller may hand `sendGtm.*` a partial identity with no id at all
 * (`{ email }` on a failed login, say) — that still emits the email, as it always has.
 */
const IDENTITY_STATUS = { identified: 'identified', anonymous: 'anonymous', unresolved: 'unresolved' } as const

type GtmIdentity =
  | ({ status: typeof IDENTITY_STATUS.identified; id: string; role: UserRole } & GtmIdentityFields)
  | ({ status: typeof IDENTITY_STATUS.anonymous; role: UserRole | null } & GtmIdentityFields)
  | ({ status: typeof IDENTITY_STATUS.unresolved; role: UserRole | null } & GtmIdentityFields)

const normalizeGtmUser = (input: NonNullable<GtmIdentityInput>): GtmUserPayload => {
  // `.profile` before `._id`: an IUser carries both.
  if ('profile' in input) {
    return {
      id: input._id || null,
      email: input.profile?.email || null,
      country: input.profile?.country?.code || null,
      language: input.profile?.language || null,
      role: input.rbac?.role || null,
      gaId: input.gaId || input.profile?.gaId || null,
    }
  }

  if ('_id' in input) {
    return {
      id: input._id || null,
      email: input.email || null,
      country: input.country?.code || null,
      language: input.language || null,
      role: input.rbac?.role || input.role || null,
      gaId: input.gaId || null,
    }
  }

  return {
    id: input.id || null,
    email: input.email || null,
    country: input.country || null,
    language: input.language || null,
    role: input.role || null,
    gaId: input.gaId || null,
  }
}

const EMPTY_IDENTITY_FIELDS = { email: null, country: null, language: null, role: null, gaId: null }

const resolveGtmIdentity = (input: GtmIdentityInput): GtmIdentity => {
  if (!input) return { status: IDENTITY_STATUS.unresolved, ...EMPTY_IDENTITY_FIELDS }

  const { id, ...fields } = normalizeGtmUser(input)

  if (!id) return { status: IDENTITY_STATUS.unresolved, ...fields }
  if (!fields.role || !isIdentifiedUser(fields.role)) return { status: IDENTITY_STATUS.anonymous, ...fields }

  return { status: IDENTITY_STATUS.identified, ...fields, id, role: fields.role }
}

const toGtmUserPayload = (identity: GtmIdentity): GtmUserPayload => ({
  id: identity.status === IDENTITY_STATUS.identified ? identity.id : null,
  email: identity.email,
  country: identity.country,
  language: identity.language,
  role: identity.role,
  gaId: identity.gaId,
})

const getProductParams = (product: Product) => {
  return {
    id: product?._id,
    price: product?.gateways?.[0]?.price?.value,
    currency: product?.gateways?.[0]?.price?.currency,
    sku: product?.gateways?.[0]?.sku,
    name: product?.name?.original,
    type: product?.type,
  }
}

const getOrderParams = (order: Order) => {
  return {
    id: order?._id,
    status: order?.status,
  }
}

export function useTagManager() {
  const { profile } = useUser()
  const { country } = useGeolocation()
  const device = getLocalStorageData(`_${settings.app.code}:device`)

  const identity = useMemo(() => resolveGtmIdentity(profile), [profile])

  // Store latest values in refs to keep sendGtm stable
  const identityRef = useRef(identity)
  const deviceRef = useRef(device)
  const countryRef = useRef(country)

  // Update refs when values change
  useEffect(() => {
    identityRef.current = identity
  }, [identity])

  useEffect(() => {
    deviceRef.current = device
  }, [device])

  useEffect(() => {
    countryRef.current = country
  }, [country])

  // Create stable sendGtm object that reads from refs
  const sendGtm = useMemo(() => {
    return (Object.keys(GTM_EVENTS) as GtmEventType[]).reduce((acc, event) => {
      acc[event] = (data: Partial<GtmDataVariables>) => {
        const { user, order, product, ...rest } = data
        const resolvedIdentity = user ? resolveGtmIdentity(user) : identityRef.current

        if (GTM_EVENTS[event].requires === EVENT_GATES.resolved && resolvedIdentity.status === IDENTITY_STATUS.unresolved) {
          return
        }

        const orderParams = order?._id ? getOrderParams(order) : null
        const productParams = product?._id ? getProductParams(product) : null

        const eventData = {
          event,
          data: {
            ...rest,
            // An order/product with no `_id` has no params to convert to, and
            // stays raw under `data` rather than being dropped from the push.
            ...(order && !order._id && { order }),
            ...(product && !product._id && { product }),
            appCode: settings?.app?.code,
            device: { id: deviceRef.current || '' },
            country: countryRef.current || '',
          },
          user: toGtmUserPayload(resolvedIdentity),
          ...(productParams && { product: productParams }),
          ...(orderParams && { order: orderParams }),
        }

        debug.log('🏷️ GTM Event:', JSON.stringify(eventData, null, 2))
        sendGTMEvent(eventData)
      }
      return acc
    }, {} as SendGtm)
  }, [])

  return { sendGtm, clearGtmUserData, pushGtmUserIdentity }
}

/**
 * Keyless dataLayer push (no `event` key): updates GTM's data model without
 * firing any trigger. GTM retains variables across pushes, so on logout the
 * previous user's identity and Intercom hash must be explicitly nulled —
 * otherwise any tag firing later in the anonymous session resolves the prior
 * user's values (PRO-191 review: stale `user_hash` could boot Intercom as the
 * previous user on a shared browser). Module-level so its identity is stable
 * for effect dependency arrays.
 *
 * Payload shape mirrors what identify pushes actually write: `sendGtm.*`
 * never pushes caller args verbatim — `eventData` (above) nests every
 * caller key under `data`, so the hash lives at `data.intercomHash` and
 * `user` is top-level. Keep this clear in sync with that construction.
 */
function clearGtmUserData() {
  debug.log('🏷️ GTM user data cleared')
  sendGTMEvent({ user: null, data: { intercomHash: null } })
}

/**
 * Keyless dataLayer push (no `event` key), the identify counterpart to
 * `clearGtmUserData`: it writes the id into GTM's data model without
 * evaluating a single trigger. Keyless matters even more here than on the
 * clear — a keyed push would hit the liga1 container's catch-all `.+` Custom
 * Event trigger and inflate the very `page_view` counts this identity write
 * exists to make accurate.
 *
 * Only the id is written: GTM merges nested objects across pushes, so the
 * later USER_SESSION_IDENTIFIED still supplies email/country/role/gaId, and
 * nothing beyond the internal id reaches the data model early.
 *
 * Module-level so its identity is stable for effect dependency arrays.
 */
function pushGtmUserIdentity(userId: string) {
  debug.log('🏷️ GTM user identity pushed:', userId)
  sendGTMEvent(buildGtmIdentityPush(userId))
}
